import { is } from 'bpmn-js/lib/util/ModelUtil';
import { isExpanded } from 'bpmn-js/lib/util/DiUtil';
import { getBreCode, isBreHandlerEntry, breSortKey } from './bre';

/**
 * Modelling side of Business Rule Exceptions.
 *
 * The reading side (bre.js, BreNavigationProvider, BreListGroup) was built for
 * Signavio imports, where the converter writes the markers. These operations
 * produce the very same shape from inside the editor, so an exception drawn by
 * hand behaves exactly like an imported one:
 *
 *   - one collapsed sub-process carrying custom:breHandler — the container
 *   - inside it one bpmn:StartEvent per exception, carrying custom:breCode,
 *     its description in bpmn:documentation
 *   - in the process an event with the same custom:breCode — the trigger
 */

export const HANDLER_NAME = 'BusinessRuleExceptions';

/** Elements that may throw a BRE. Kept to events: `custom:breCode` is declared
 *  on bpmn:Event, and the Signavio export only ever throws from one. */
const TRIGGER_TYPES = ['bpmn:EndEvent', 'bpmn:IntermediateThrowEvent', 'bpmn:BoundaryEvent'];

export function canTriggerBre(element) {
  if (!element || element.type === 'label') return false;
  return TRIGGER_TYPES.some((type) => is(element, type));
}

export function isHandlerContainer(element) {
  return is(element, 'bpmn:SubProcess')
    && element.businessObject?.get?.('custom:breHandler') === true;
}

/** The diagram's BRE container, or null while none has been drawn yet. */
export function findHandlerContainer(elementRegistry) {
  let found = null;
  elementRegistry.forEach((el) => {
    if (!found && isHandlerContainer(el)) found = el;
  });
  return found;
}

/** Every code already taken, whether by a handler entry or by a trigger. */
export function usedCodes(elementRegistry) {
  const codes = new Set();
  elementRegistry.forEach((el) => {
    const code = getBreCode(el);
    if (code) codes.add(code);
  });
  return codes;
}

/** The lowest free BREn — a deleted code is handed out again. */
export function nextFreeCode(elementRegistry) {
  const taken = usedCodes(elementRegistry);
  let n = 1;
  while (taken.has(`BRE${n}`)) n += 1;
  return `BRE${n}`;
}

/** Handler entries of the diagram, numerically sorted (BRE2 before BRE10). */
export function listHandlerEntries(elementRegistry) {
  const entries = [];
  elementRegistry.forEach((el) => {
    if (isBreHandlerEntry(el)) entries.push(el);
  });
  return entries.sort((a, b) => breSortKey(getBreCode(a)) - breSortKey(getBreCode(b)));
}

function documentationText(element) {
  return element?.businessObject?.documentation?.[0]?.text || '';
}

export function breDescription(element) {
  return documentationText(element);
}

/** Where the container goes: below everything already on the current plane. */
function containerPosition(canvas, elementRegistry) {
  const root = canvas.getRootElement();
  let left = Infinity;
  let bottom = -Infinity;
  elementRegistry.forEach((el) => {
    if (el === root || el.parent !== root || el.waypoints || el.type === 'label') return;
    if (typeof el.x !== 'number' || typeof el.y !== 'number') return;
    left = Math.min(left, el.x);
    bottom = Math.max(bottom, el.y + (el.height || 0));
  });
  if (!Number.isFinite(left)) return { x: 300, y: 300 };
  return { x: left + 80, y: bottom + 140 };
}

/**
 * The container, created on first use.
 *
 * Drawn expanded and then collapsed, because `toggleCollapse` is what creates
 * the drill-down plane — a sub-process born collapsed would have nowhere to put
 * its entries.
 */
export function ensureHandlerContainer(injector) {
  const elementRegistry = injector.get('elementRegistry');
  const existing = findHandlerContainer(elementRegistry);
  if (existing) return existing;

  const modeling = injector.get('modeling');
  const elementFactory = injector.get('elementFactory');
  const canvas = injector.get('canvas');

  const shape = elementFactory.createShape({ type: 'bpmn:SubProcess', isExpanded: true });
  const created = modeling.createShape(
    shape,
    containerPosition(canvas, elementRegistry),
    canvas.getRootElement(),
  );
  modeling.updateProperties(created, { name: HANDLER_NAME, 'custom:breHandler': true });
  modeling.toggleCollapse(created);
  return created;
}

/** The drill-down plane of a collapsed sub-process. */
export function planeOf(injector, container) {
  const planeId = `${container.id}_plane`;
  const fromRegistry = injector.get('elementRegistry').get(planeId);
  if (fromRegistry) return fromRegistry;
  const canvas = injector.get('canvas');
  return canvas.getRootElements().find((root) => root.id === planeId) || null;
}

/**
 * Where a new entry goes.
 *
 * A collapsed container keeps its children on a drill-down plane; an expanded
 * one keeps them inside the shape itself. Looking only for the plane would
 * leave the button dead as soon as the user expands the container.
 */
function entryParent(injector, container) {
  if (isExpanded(container)) return container;
  const plane = planeOf(injector, container);
  if (!plane) console.warn('[BRE] no drill-down plane for', container.id);
  return plane;
}

/**
 * Nudge the properties panel into re-rendering.
 *
 * It only redraws for the element it currently shows. Creating or deleting a
 * handler entry changes something *elsewhere* in the diagram, so without this
 * the list would silently stay as it was — and the button would look dead.
 */
function refreshPanel(injector) {
  try {
    const selected = injector.get('selection').get();
    const shown = selected.length > 0 ? selected : [injector.get('canvas').getRootElement()];
    injector.get('eventBus').fire('elements.changed', { elements: shown });
  } catch { /* panel absent (viewer) — nothing to refresh */ }
}

function setDescription(injector, element, text) {
  const modeling = injector.get('modeling');
  const bpmnFactory = injector.get('bpmnFactory');
  const bo = element.businessObject;
  const documentation = text
    ? [bpmnFactory.create('bpmn:Documentation', { text })]
    : [];
  modeling.updateModdleProperties(element, bo, { documentation });
}

const ENTRY_SPACING = 110;

/** Stack entries in a column — on the plane from its origin, inside an
 *  expanded container from the container's own upper left. */
function entryPosition(container, parent, siblings) {
  const offsetY = 60 + siblings * ENTRY_SPACING;
  if (parent === container) {
    return { x: container.x + 80, y: container.y + offsetY };
  }
  return { x: 200, y: 100 + offsetY };
}

/** Keep an expanded container big enough for the column of entries. */
function growContainer(injector, container) {
  const needed = 60 + (container.children || []).filter(isBreHandlerEntry).length * ENTRY_SPACING;
  if (container.height >= needed) return;
  injector.get('modeling').resizeShape(container, {
    x: container.x,
    y: container.y,
    width: container.width,
    height: needed,
  });
}

/**
 * Add one exception. Returns the created handler entry, or null when the
 * container offers nowhere to put it.
 */
export function createBre(injector, { name = '', description = '' } = {}) {
  const elementRegistry = injector.get('elementRegistry');
  const modeling = injector.get('modeling');
  const elementFactory = injector.get('elementFactory');
  const canvas = injector.get('canvas');

  const code = nextFreeCode(elementRegistry);
  // Where the user is looking right now. Drawing into another root switches the
  // active plane, which would drag them into the container unasked.
  const rootBefore = canvas.getRootElement();
  const container = ensureHandlerContainer(injector);
  const parent = entryParent(injector, container);
  if (!parent) return null;

  const siblings = listHandlerEntries(elementRegistry).filter(
    (el) => el.parent && el.parent.id === parent.id,
  ).length;

  const entry = modeling.createShape(
    elementFactory.createShape({ type: 'bpmn:StartEvent' }),
    entryPosition(container, parent, siblings),
    parent,
  );
  modeling.updateProperties(entry, { name: name || code, 'custom:breCode': code });
  if (description) setDescription(injector, entry, description);
  if (parent === container) growContainer(injector, container);

  if (rootBefore && canvas.getRootElement() !== rootBefore) canvas.setRootElement(rootBefore);
  refreshPanel(injector);
  return entry;
}

export function updateBre(injector, entry, { name, description }) {
  const modeling = injector.get('modeling');
  if (name !== undefined) modeling.updateProperties(entry, { name });
  if (description !== undefined) setDescription(injector, entry, description);
  refreshPanel(injector);
}

/**
 * Remove one exception — the entry and every trigger pointing at it. A trigger
 * left behind would keep a code that describes nothing.
 */
export function removeBre(injector, entry) {
  const elementRegistry = injector.get('elementRegistry');
  const modeling = injector.get('modeling');
  const code = getBreCode(entry);

  const triggers = [];
  elementRegistry.forEach((el) => {
    if (el !== entry && !isBreHandlerEntry(el) && getBreCode(el) === code) triggers.push(el);
  });
  triggers.forEach((el) => clearTrigger(injector, el));
  modeling.removeElements([entry]);
  refreshPanel(injector);
}

/** Point an event at an exception. */
export function assignTrigger(injector, element, code) {
  injector.get('modeling').updateProperties(element, { 'custom:breCode': code });
}

/** Detach an event from its exception, leaving the event itself in place. */
export function clearTrigger(injector, element) {
  const modeling = injector.get('modeling');
  modeling.updateProperties(element, { 'custom:breCode': undefined });
  // The read side falls back to the name, so a leading "BRE1" would keep the
  // element linked even after the attribute is gone. Only that token is taken;
  // anything the user wrote after it is theirs and stays.
  const name = element.businessObject?.name || '';
  const rest = name.replace(/^\s*BRE\d+\b[\s—–-]*/i, '');
  if (rest !== name) modeling.updateProperties(element, { name: rest });
}
